import React from "react";
import { motion } from "framer-motion";
import { problems, copingChart, blockedChart, blockedCopy, blockedTakeaway } from "../../data/reportData";
import { CopingBars, BlockerRings, CountUp } from "../charts/Charts";
import { ArrowDown } from "lucide-react";
import { CircledWord } from "../CircledWord";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

function MedianFigure() {
  const [done, setDone] = React.useState(false);
  return (
    <CircledWord color="#53B851" show={done}>
      <span className="font-display font-medium leading-none tracking-[-0.02em] text-[44px] md:text-[58px]">
        <CountUp value={20000} from={5000} prefix="$" duration={1.05} onDone={() => setDone(true)} />
      </span>
    </CircledWord>
  );
}

function SplitTitle({ title, highlight, color }) {
  const idx = title.indexOf(highlight);
  if (idx === -1) return title;
  return (
    <>
      {title.slice(0, idx)}
      <CircledWord color={color}>{highlight}</CircledWord>
      {title.slice(idx + highlight.length)}
    </>
  );
}

const themes = {
  dark: {
    bg: "radial-gradient(ellipse 48% 44% at 100% 0%, #395B41 0%, rgba(30,48,35,0.55) 45%, rgba(0,0,0,0) 72%), #010101",
    heading: "#FFFFFF",
    body: "#C9CEC9",
    list: "#F2F2F2",
    divider: "rgba(255,255,255,0.22)",
    btnBg: "#FFFFFF",
    btnInk: "#0A0A0A",
    btnHover: "#EDEFED",
    marker: "#E9EDE9",
  },
  light: {
    bg: "radial-gradient(ellipse 48% 44% at 100% 0%, #E0F7E7 0%, rgba(240,253,244,0.55) 52%, rgba(255,255,255,0) 78%), #FFFFFF",
    heading: "#22382A",
    body: "#3F5546",
    list: "#16211A",
    divider: "rgba(10,10,10,0.15)",
    btnBg: "#0A0A0A",
    btnInk: "#FFFFFF",
    btnHover: "#1c1c1c",
    marker: "#22382A",
  },
};

function FailureCard({ p, i, total }) {
  const t = themes[p.theme];

  const scrollNext = () => {
    const el = document.getElementById(i < total - 1 ? `failure-card-${i + 1}` : "workaround");
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (i < total - 1 ? 0 : 90), behavior: "smooth" });
  };

  return (
    <div id={`failure-card-${i}`} className="md:sticky md:top-0 md:h-screen snap-card" style={{ zIndex: i + 1 }}>
      <motion.div
        style={{ background: t.bg, color: t.heading }}
        className="h-full min-h-[100svh] md:min-h-0 grid md:grid-cols-2 overflow-hidden"
        data-testid={`failure-card-${p.n}`}
      >
        {/* left: encircled letter marker + leaf image anchored bottom-left */}
        <div className="relative max-md:order-2 max-md:h-[42vh]">
          <span
            className="absolute top-6 left-5 md:top-[4.75vw] md:left-14 inline-flex items-center justify-center w-[68px] h-[34px]"
            style={{ color: t.marker }}
          >
            <span className="text-[15px] leading-none">{p.n}</span>
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 68 34" fill="none" aria-hidden="true">
              <ellipse cx="34" cy="17" rx="32.5" ry="14.5" stroke={p.accent} strokeOpacity="0.55" strokeWidth="1" transform="rotate(-4 34 17)" />
            </svg>
          </span>
          <div className="absolute bottom-0 left-0 w-[88%] h-[58%] max-md:w-full max-md:h-full overflow-hidden">
            <img src={p.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>

        {/* right: content */}
        <div className="flex flex-col justify-between px-5 pb-10 pt-20 md:pl-[8%] md:pr-[11%] md:pb-11 md:pt-[9.5%] max-md:order-1">
          <div>
            <h3
              className="font-display font-medium tracking-[-0.02em] leading-[1.06]"
              style={{ fontSize: "clamp(30px, 3.35vw, 66px)" }}
            >
              <SplitTitle title={p.title} highlight={p.highlight} color={p.accent} />
            </h3>
            <p className="text-[16px] md:text-[18px] leading-[1.75] mt-6 md:mt-9 max-w-2xl" style={{ color: t.body }}>
              {p.body}
            </p>
          </div>

          <div className="mt-10">
            <p className="text-[13px] md:text-[14.5px] tracking-[0.14em] mb-2" style={{ color: p.accent }}>
              {p.eyebrow}
            </p>
            <ul>
              {p.examples.map((ex) => (
                <li
                  key={ex}
                  className="py-4 md:py-[18px] text-[15.5px] md:text-[18px] leading-snug"
                  style={{ borderBottom: `1px solid ${t.divider}`, color: t.list }}
                >
                  {ex}
                </li>
              ))}
            </ul>
            <button
              onClick={scrollNext}
              className="group w-full mt-8 flex items-center justify-between px-6 md:px-11 py-5 md:py-[30px] text-[15px] md:text-[17.5px] transition-colors"
              style={{ background: t.btnBg, color: t.btnInk, "--btn-hover": t.btnHover }}
              onMouseEnter={(e) => (e.currentTarget.style.background = t.btnHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = t.btnBg)}
              data-testid={`failure-next-${p.n}`}
            >
              <span>{p.next}</span>
              <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function ProblemSection() {
  return (
    <>
      {/* ── Three failures ── */}
      <section id="problem" data-testid="section-problem">
        <div className="problem-intro">
          <motion.p
            {...fadeUp}
            className="problem-intro__eyebrow"
            data-testid="problem-intro-eyebrow"
          >
            The problem
          </motion.p>
          <motion.h2
            {...fadeUp}
            className="problem-intro__title"
            data-testid="problem-intro-title"
          >
            Different businesses, same three failures
          </motion.h2>
          <motion.p
            {...fadeUp}
            className="problem-intro__copy"
            data-testid="problem-intro-copy"
          >
            Underneath the industries, the answers narrowed to three problems almost every operator recognized.
          </motion.p>
        </div>

        <div id="problem-stack" className="relative" data-testid="problem-cards">
          {problems.map((p, i) => (
            <FailureCard key={p.n} p={p} i={i} total={problems.length} />
          ))}
        </div>
      </section>

      {/* ── How they coped ── */}
      <section id="workaround" className="report-col section-gap" data-testid="section-workaround">
        <div className="md:pl-[8%]">
          <motion.p
            {...fadeUp}
            className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
            style={{ color: "#00B843" }}
            data-testid="workaround-eyebrow"
          >
            How they coped
          </motion.p>
          <motion.h2 {...fadeUp} className="section-h2">
            Every workaround came at a cost
          </motion.h2>
          <motion.p {...fadeUp} className="body-text mt-8">
            Only <span className="text-[#00B843] font-semibold">one in seven</span> of the 300 operators interviewed was genuinely well served by software already on the market. Everyone else adapted. They accepted manual work, compromised on software that only half-fit, or paid for custom development. None of those choices solved the underlying problem.
          </motion.p>

          <motion.div {...fadeUp} className="mt-16 md:mt-24 md:mr-[11%]" data-testid="coping-chart-title">
            <h3 className="font-display font-medium text-[22px] md:text-[26px] tracking-[-0.01em] text-[#0A0A0A]">
              {copingChart.title}
            </h3>
            <div className="h-px bg-[#E3E6EA] mt-4 md:mt-5" />
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-10 md:mt-12" data-testid="coping-chart">
          <CopingBars
            labels={copingChart.labels}
            values={copingChart.values}
            widths={copingChart.widths}
            source={copingChart.source}
          />
        </motion.div>
      </section>

      {/* ── Out of reach ── */}
      <section id="blocked" className="report-col section-gap" data-testid="section-blocked">
        <motion.p
          {...fadeUp}
          className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
          style={{ color: "#00B843" }}
          data-testid="blocked-eyebrow"
        >
          Out of reach
        </motion.p>
        <motion.h2 {...fadeUp} className="section-h2">
          Why a real fix stayed out of reach
        </motion.h2>

        <motion.p {...fadeUp} className="body-text mt-6">{blockedCopy[0]}</motion.p>

        {/* price scale card */}
        <motion.div
          {...fadeUp}
          className="my-12 md:my-14 rounded-lg px-6 py-8 md:px-10 md:py-9"
          style={{ background: "#EAFFED" }}
          data-testid="price-range-callout"
        >
          <p className="uppercase text-[11.5px] md:text-[12.5px] tracking-[0.16em] text-[#424843]">
            Typical developer agency quote, basic build
            <span className="ml-3 font-medium" style={{ color: "#53B851" }}>The median</span>
          </p>

          <div className="flex flex-col items-center mt-7 md:mt-8" data-testid="price-median-marker">
            <MedianFigure />
            <motion.span
              className="w-[2px] h-6 md:h-7 mt-4"
              style={{ background: "#53B851" }}
              initial={{ opacity: 0, scaleY: 0 }}
              whileInView={{ opacity: 1, scaleY: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.9, duration: 0.5 }}
            />
          </div>

          {/* track with end dots */}
          <div className="relative">
            <motion.div
              className="h-[2px] bg-[#0A0A0A] origin-left"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            />
            {["left-0", "right-0"].map((pos) => (
              <motion.span
                key={pos}
                className={`absolute ${pos} top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#0A0A0A]`}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: pos === "left-0" ? 0.15 : 1.0, duration: 0.4 }}
              />
            ))}
          </div>

          <div className="flex justify-between items-baseline mt-3">
            {["$5,000", "$100,000"].map((v, i) => (
              <motion.span
                key={v}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i === 0 ? 0.2 : 1.05, duration: 0.5 }}
                className="font-display font-medium text-[22px] md:text-[28px] leading-none tracking-[-0.02em] text-[#0A0A0A]"
              >
                {v}
              </motion.span>
            ))}
          </div>

          <p className="uppercase text-[10.5px] md:text-[11.5px] tracking-[0.16em] text-[#747775] mt-7">
            Quotes reported by operators. <span className="font-medium" style={{ color: "#53B851" }}>$20,000 is the median</span>, not the mean.
          </p>
        </motion.div>

        <motion.p {...fadeUp} className="body-text">{blockedCopy[1]}</motion.p>

        {/* blocker rings chart */}
        <motion.div {...fadeUp} className="mt-16 md:mt-24" data-testid="blocked-chart-title">
          <h3 className="font-display font-medium text-[22px] md:text-[26px] tracking-[-0.01em] text-[#0A0A0A]">
            {blockedChart.title}
          </h3>
          <p className="text-[14px] md:text-[15px] text-[#7E7E7B] mt-2">{blockedChart.subtitle}</p>
          <div className="h-px bg-[#E3E6EA] mt-4 md:mt-5" />
        </motion.div>

        <motion.div {...fadeUp} className="mt-10 md:mt-12" data-testid="blocked-chart">
          <BlockerRings labels={blockedChart.labels} values={blockedChart.values} />
          <p className="mt-8 md:mt-10 text-[14px] md:text-[15px] text-[#808080]" data-testid="blocked-source">
            Source: <span className="font-semibold" style={{ color: "#53B851" }}>300</span> structured operator interviews
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          className="mt-12 md:mt-16 pl-6 md:pl-8 py-1"
          style={{ borderLeft: "4px solid #53B851" }}
          data-testid="blocked-takeaway"
        >
          <p className="font-display font-medium text-[22px] md:text-[26px] leading-[1.3] tracking-[-0.01em] text-[#0A0A0A]">
            {blockedTakeaway}
          </p>
        </motion.div>
      </section>
    </>
  );
}
